import React, { ComponentProps, useEffect, useState } from 'react'
import { AntdLayout, Menu, Grid, Icons } from '@pankod/refine-antd'
import { SiderProps } from 'antd'
import {
  useLogout,
  useTitle,
  ITreeMenu,
  useIsExistAuthentication,
  useCanWithoutCache,
  useRouterContext,
  useMenu,
  useRefineContext,
} from '@pankod/refine-core'
import clsx from 'clsx'
import Image from 'next/image'

import { Title as DefaultTitle } from '../title'
import { antLayoutSider, antLayoutSiderMobile } from './styles'
import { getTheme } from 'src/helpers/theme'
import { isPanduanLmsAdminEnabled, useGetSelectedKey } from './utils'

const { LogoutOutlined, UnorderedListOutlined } = Icons
const PANDUAN_LMS_ADMIN_ROUTE = '/panduan-lms-admin'

type ItemType = NonNullable<ComponentProps<typeof Menu>['items']>[number]

type Sider = {
  selectedKeys?: string[]
  defaultOpenKeys?: string[]
}

export const Sider: React.FC<Sider> = (props) => {
  const [collapsed, setCollapsed] = useState<boolean>(false)
  const isExistAuthentication = useIsExistAuthentication()
  const { Link } = useRouterContext()
  const { hasDashboard } = useRefineContext()
  const { mutate: logout } = useLogout()
  const Title = useTitle()
  const { menuItems, selectedKey, defaultOpenKeys } = useMenu()
  const breakpoint = Grid.useBreakpoint()
  const { can: checkCanAccess } = useCanWithoutCache()
  const [renderedMenuItems, setRenderedMenuItems] = React.useState<
    Array<ItemType>
  >([])
  const getSelectedKey = useGetSelectedKey(menuItems)
  const shouldRenderPanduanLmsAdmin = isPanduanLmsAdminEnabled(
    process.env.NEXT_PUBLIC_ZENDESK_LMS_ADMIN_SECTION_ID,
  )

  const isMobile = !breakpoint.lg
  const resolvedDefaultOpenKeys =
    shouldRenderPanduanLmsAdmin && selectedKey === PANDUAN_LMS_ADMIN_ROUTE
      ? Array.from(
          new Set([
            ...(props.defaultOpenKeys || defaultOpenKeys),
            'pusat-bantuan',
          ]),
        )
      : props.defaultOpenKeys || defaultOpenKeys

  const RenderToTitle = Title ?? DefaultTitle

  const handleTreeMenuItem = React.useCallback(
    async (
      item: ITreeMenu & { type?: 'divider' | 'group' },
      selectedKey?: string,
    ): Promise<ItemType | undefined> => {
      if (item.children?.length > 0) {
        const handledChildren = (
          await Promise.all(
            item.children.map((child) =>
              handleTreeMenuItem(child, selectedKey),
            ),
          )
        ).filter(Boolean)
        // is sub
        if (handledChildren.length > 0) {
          return {
            key: item.route,
            label: item.label,
            icon: item.icon ?? <UnorderedListOutlined />,
            title: item.label,
            children: handledChildren,
            type: item.type,
          } as ItemType
        }

        return undefined
      } else {
        let isSelected = item.route === selectedKey
        if (item.options?.include) {
          isSelected = isSelected || item.options.include.includes(selectedKey)
        }
        const isRoute = !(
          item.parentName !== undefined && item.children.length === 0
        )

        const { can: hasAccess } = checkCanAccess
          ? await checkCanAccess({
              resource: item.name,
              action: 'list',
            })
          : { can: true }
        // is leaf
        if (hasAccess) {
          const LabelWrapper = item.route
            ? Link
            : (React.Fragment as typeof Link)
          return {
            key: item.route,
            label: (
              <LabelWrapper href={item.route} to={item.route}>
                {item.label}
                {isSelected && <div className="ant-menu-tree-arrow" />}
              </LabelWrapper>
            ),
            icon: item.icon ?? (isRoute && <UnorderedListOutlined />),
            className: clsx(
              isSelected && 'ant-menu-item-selected font-bold',
              'first:!mt-0',
            ),
          } as ItemType
        }
        return undefined
      }
    },
    [checkCanAccess, Link],
  )

  const renderTree = React.useCallback(
    async (items: ITreeMenu[], selected: string) => {
      const promises = items.map((item) => handleTreeMenuItem(item, selected))
      setRenderedMenuItems(
        (await Promise.all(promises)).filter(Boolean) as ItemType[],
      )
    },
    [handleTreeMenuItem],
  )

  useEffect(() => {
    renderTree(menuItems, selectedKey)
  }, [menuItems, renderTree, selectedKey])
  return (
    <AntdLayout.Sider
      collapsible
      collapsed={collapsed}
      onCollapse={(collapsed: boolean): void => setCollapsed(collapsed)}
      collapsedWidth={isMobile ? 0 : 80}
      breakpoint="lg"
      style={isMobile ? antLayoutSiderMobile : antLayoutSider}
      width={250}
      theme={getTheme() as SiderProps['theme']}
    >
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <RenderToTitle collapsed={collapsed} />
        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
          <Menu
            selectedKeys={props.selectedKeys || [getSelectedKey(selectedKey)]}
            defaultOpenKeys={resolvedDefaultOpenKeys}
            mode="inline"
            onClick={() => {
              if (!breakpoint.lg) {
                setCollapsed(true)
              }
            }}
            items={[
              ...(hasDashboard
                ? [
                    {
                      key: 'Homepage',
                      className: clsx(
                        selectedKey === '/' &&
                          'ant-menu-item-selected font-bold',
                        'first:!mt-0',
                      ),
                      icon:
                        selectedKey === '/' ? (
                          <Icons.HomeTwoTone />
                        ) : (
                          <Icons.HomeFilled />
                        ),
                      label: (
                        <Link href="/" to="/">
                          Homepage
                          {selectedKey === '/' && (
                            <div className="ant-menu-tree-arrow" />
                          )}
                        </Link>
                      ),
                    } as ItemType,
                  ]
                : []),
              ...renderedMenuItems,
              {
                key: 'div-1',
                type: 'divider',
                className: '!my-4',
              },
              {
                key: 'pusat-bantuan',
                icon: <Icons.CustomerServiceOutlined />,
                label: 'Pusat Bantuan',
                children: [
                  ...(shouldRenderPanduanLmsAdmin
                    ? [
                        {
                          key: PANDUAN_LMS_ADMIN_ROUTE,
                          className: clsx(
                            selectedKey === PANDUAN_LMS_ADMIN_ROUTE &&
                              'ant-menu-item-selected font-bold',
                          ),
                          label: (
                            <Link
                              href={PANDUAN_LMS_ADMIN_ROUTE}
                              to={PANDUAN_LMS_ADMIN_ROUTE}
                            >
                              Panduan LMS Admin
                              {selectedKey === PANDUAN_LMS_ADMIN_ROUTE && (
                                <div className="ant-menu-tree-arrow" />
                              )}
                            </Link>
                          ),
                        },
                      ]
                    : [
                        {
                          key: 'panduan-seputar-lms',
                          label: (
                            <Link
                              href={
                                process.env
                                  .NEXT_PUBLIC_PUSAT_INFORMASI_LMS_BASE_URL +
                                '/hc/id/categories/52398726809113-LMS-Guru'
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Panduan Seputar LMS
                            </Link>
                          ),
                        },
                      ]),
                ],
              },

              ...(isExistAuthentication
                ? [
                    {
                      key: 'logout',
                      label: 'Logout',
                      onClick: () => logout(),
                      danger: true,
                      icon: <LogoutOutlined />,
                    } as ItemType,
                  ]
                : []),
            ]}
          />
        </div>

        <div
          style={{
            padding: collapsed ? '16px 8px' : '16px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            marginBottom: 60,
          }}
        >
          <Image
            alt="BSrE Logo"
            src={collapsed ? '/img/BSrE-logo-only.png' : '/img/BSrE-logo.png'}
            width={collapsed ? 32 : 120}
            height={collapsed ? 32 : 40}
            className="rounded-lg"
            style={{ objectFit: 'contain' }}
          />
        </div>
      </div>
    </AntdLayout.Sider>
  )
}
